import { useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from '../lib/gsapConfig';

const coverage = [
  'Local city moves',
  'In-state routes',
  'Cross-country relocation',
  'Commercial transport support',
  'Packing and handling',
  'Apartment and condo moves',
];

const hubs = [
  { city: 'Los Angeles, CA', abbr: 'LA',  top: '30%', left: '12%' },
  { city: 'Chicago, IL',     abbr: 'CHI', top: '40%', left: '44%' },
  { city: 'New York, NY',    abbr: 'NY',  top: '22%', left: '80%' },
  { city: 'Dallas, TX',      abbr: 'DAL', top: '62%', left: '28%' },
];

export default function CoverageSection() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from('.coverage-copy, .coverage-map, .coverage-pill', {
        scrollTrigger: { trigger: sectionRef.current, start: 'top 78%' },
        y: 34,
        opacity: 0,
        duration: 0.65,
        stagger: 0.07,
        ease: 'power3.out',
      });

      gsap.fromTo(
        '.coverage-truck',
        { xPercent: -7, yPercent: 3.5 },
        { xPercent: 7, yPercent: -3.5, duration: 4, repeat: -1, yoyo: true, ease: 'sine.inOut' }
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <section ref={sectionRef} className="bg-slate-50 py-12 sm:py-16">
      <div className="container mx-auto px-4">
        <div className="grid gap-8 lg:grid-cols-[1fr_1fr] lg:items-center">

          {/* ── Right copy — order-1 on mobile so heading shows first ── */}
          <div className="order-1 lg:order-2">
            <span className="coverage-copy inline-flex rounded-full bg-white px-4 py-2 text-sm font-bold text-primary shadow-sm">
              Service area and trucking coverage
            </span>
            <h2 className="coverage-copy mt-4 text-2xl font-extrabold tracking-normal text-slate-950 sm:text-3xl lg:text-4xl">
              We cover every move, local routes to long-distance hauls
            </h2>
            <p className="coverage-copy mt-3 text-base leading-7 text-slate-600 sm:text-lg sm:leading-8">
              Whether you're moving across town or across the country, our verified movers operate in all 50 states. From apartment moves to full commercial relocations, there's a mover ready for your route.
            </p>

            <div className="mt-5 grid grid-cols-2 gap-2 sm:gap-3">
              {coverage.map((item) => (
                <div key={item} className="coverage-pill flex items-center gap-2 sm:gap-3 rounded-xl border border-slate-200 bg-white px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-bold text-slate-700 shadow-sm">
                  <span className="flex h-5 w-5 sm:h-6 sm:w-6 shrink-0 items-center justify-center rounded-full bg-green-50 text-green-700">
                    <svg className="h-3 w-3 sm:h-4 sm:w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.4" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  {item}
                </div>
              ))}
            </div>

            <Link
              to="/quote"
              className="coverage-copy mt-6 inline-flex items-center rounded-lg bg-primary px-5 py-3 text-sm font-extrabold text-white shadow-lg shadow-primary/20 transition hover:bg-primary-dark"
            >
              Check your route
              <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
              </svg>
            </Link>
          </div>

          {/* ── Map card — order-2 on mobile ── */}
          <div className="order-2 lg:order-1">
            <div className="coverage-map relative min-h-[300px] sm:min-h-[380px] overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-[0_12px_36px_rgba(15,23,42,0.08)]">
              {/* Grid background */}
              <div className="absolute inset-0 bg-[linear-gradient(90deg,rgba(148,163,184,0.12)_1px,transparent_1px),linear-gradient(rgba(148,163,184,0.12)_1px,transparent_1px)] bg-[size:44px_44px]" />

              {/* Routes SVG */}
              <svg className="absolute inset-0 h-full w-full" viewBox="0 0 520 360" fill="none" aria-hidden="true">
                <path d="M62 108 C130 85 190 150 229 144 C275 138 368 84 416 79" stroke="rgba(15,23,42,0.09)" strokeWidth="18" strokeLinecap="round" />
                <path d="M62 108 C130 85 190 150 229 144 C275 138 368 84 416 79" stroke="#EB9813" strokeWidth="5" strokeLinecap="round" opacity="0.9" />
                <path d="M146 223 C168 185 198 162 229 144" stroke="rgba(59,130,246,0.45)" strokeWidth="4" strokeLinecap="round" />
                <circle cx="62"  cy="108" r="6" fill="#EB9813" opacity="0.8" />
                <circle cx="229" cy="144" r="6" fill="#EB9813" opacity="0.8" />
                <circle cx="416" cy="79"  r="6" fill="#EB9813" opacity="0.8" />
                <circle cx="146" cy="223" r="6" fill="#3b82f6"  opacity="0.55" />
                <circle cx="62"  cy="108" r="3" fill="white" />
                <circle cx="229" cy="144" r="3" fill="white" />
                <circle cx="416" cy="79"  r="3" fill="white" />
                <circle cx="146" cy="223" r="3" fill="white" />
              </svg>

              {/* City labels — desktop only (sm+) */}
              {hubs.map((hub) => (
                <div
                  key={hub.city}
                  className="absolute z-10 hidden sm:block -translate-x-1/2 -translate-y-1/2 rounded-xl border border-slate-200 bg-white px-3 py-1.5 shadow-md"
                  style={{ top: hub.top, left: hub.left }}
                >
                  <div className="flex items-center gap-1.5 whitespace-nowrap">
                    <span className="h-2 w-2 shrink-0 rounded-full bg-primary" />
                    <span className="text-xs font-black text-slate-900">{hub.city}</span>
                  </div>
                </div>
              ))}

              {/* Mobile-only city pills — shown as a row inside the map */}
              <div className="absolute top-3 left-3 right-3 z-10 flex flex-wrap gap-1.5 sm:hidden">
                {hubs.map((hub) => (
                  <div key={hub.city} className="flex items-center gap-1 rounded-full border border-slate-200 bg-white/90 px-2 py-1 shadow-sm backdrop-blur-sm">
                    <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
                    <span className="text-[10px] font-bold text-slate-800">{hub.city}</span>
                  </div>
                ))}
              </div>

              {/* Truck icon */}
              <div className="coverage-truck absolute left-[62%] top-[38%] sm:top-[30%] z-20 flex h-12 w-12 sm:h-14 sm:w-14 items-center justify-center rounded-2xl bg-slate-950 text-primary shadow-xl">
                <svg className="h-6 w-6 sm:h-8 sm:w-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 7h11v10H3zM14 10h4l3 4v3h-7V10zM7 19a2 2 0 100-4 2 2 0 000 4zM18 19a2 2 0 100-4 2 2 0 000 4z" />
                </svg>
              </div>

              {/* Bottom info card */}
              <div className="absolute bottom-3 left-3 right-3 rounded-2xl border border-slate-200 bg-white/96 p-3 shadow-xl backdrop-blur">
                <div className="flex items-center gap-2">
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-green-100">
                    <svg className="h-3.5 w-3.5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                    </svg>
                  </span>
                  <span className="text-sm font-extrabold text-slate-950">All 50 States · 500+ Active Movers</span>
                </div>
                {/* Description hidden on mobile to keep card compact */}
                <p className="hidden sm:block pl-8 mt-1 text-sm leading-6 text-slate-500">
                  Local city moves to coast-to-coast hauls. Our network of verified movers has your route covered.
                </p>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>
  );
}
